import {
  BofuDocumentRelationship,
  BofuTabDocument,
  DocumentResponse,
  FORM_STATUS_ENUM,
  PersonaStructure,
  PersonFormNodeConfigResponse,
  PersonFormsConfig,
  TabsConfigResponseData,
  TabsConfigResponseDocument,
  TabsConfigResponseForm,
} from "@interfaces/response";
import {
  ALL_FIELDS_DISABLED,
  DEFAULT_PERSON_FORMS_CONFIG,
  DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE,
  PERSON_FORM_FIELD_CODES,
  PERSON_FORM_HUB_TYPE_CODES,
  PersonFormFieldCode,
  PersonFormFieldConfig,
  PTCAV_EXCLUDED_FIELD_CODES,
} from "@components/PersonForm/personFormConstants";
import {
  generateDocumentConfig,
  generatePropietarioDocs,
  validateDocumentStructure,
} from "@components/TabBofu/functions";
import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES, PERSON_CATALOG } from "@helpers/constantes";
import { UUID } from "crypto";

export const formatPersonName = (person: PersonaStructure) =>
  `${person.name || ""} ${person.last_name || ""} ${person.last_name_2 || ""}`.trim() || "Sin nombre";

export const getPersonInitials = (person: PersonaStructure) => {
  const first = person.name?.trim()?.charAt(0) || "";
  const last = person.last_name?.trim()?.charAt(0) || "";
  return `${first}${last}`.toUpperCase() || "?";
};

export const isPersonInfoComplete = (status: FORM_STATUS_ENUM | null | undefined) =>
  status === "COMPLETO" || status === "VALIDADO";

/** Posición del código en la jerarquía del hub; -1 si el hub no lo admite. */
const getFormHubTypeIndex = (code: string) => (PERSON_FORM_HUB_TYPE_CODES as readonly string[]).indexOf(code);

/**
 * Menor índice = mayor prioridad. Si tiene varios tipos, usa el de mayor jerarquía;
 * sin tipos del hub regresa `Infinity` para quedar al final.
 */
const getPersonFormHubSortRank = (person: PersonaStructure) => {
  let best = Number.POSITIVE_INFINITY;

  for (const personType of person.person_types_detail || []) {
    const index = getFormHubTypeIndex(personType.code);
    if (index !== -1 && index < best) {
      best = index;
    }
  }

  return best;
};

/** Tipos de la persona que admite el hub TBPER (`PERSON_FORM_HUB_TYPE_CODES`), en su orden original. */
export const getPersonOnlyPersonTypesAllowedToFormHub = (person: PersonaStructure) =>
  (person.person_types_detail || []).filter((personType) => getFormHubTypeIndex(personType.code) >= 0);

/**
 * Deja las personas con algún tipo del hub, con `person_types_detail` reducido a esos tipos,
 * ordenadas por jerarquía. Regresa copias porque los objetos de RTK Query están congelados.
 */
export const filterPersonsForFormHub = (persons: PersonaStructure[]) =>
  persons
    .map((person) => ({ ...person, person_types_detail: getPersonOnlyPersonTypesAllowedToFormHub(person) }))
    .filter((person) => person.person_types_detail.length > 0)
    .sort((a, b) => getPersonFormHubSortRank(a) - getPersonFormHubSortRank(b));

/** Busca la configuración del tab TBPER dentro de la respuesta de `tabs_bofu_config`; `undefined` si no viene. */
export const findPersonasTab = (tabsConfig: TabsConfigResponseData[] | undefined) =>
  (tabsConfig || []).find((tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_personas.code);

/**
 * Relaciones de documento que pide la persona según sus tipos (p. ej. PTCAV → `conyuge_aval`).
 * Solo considera tipos definidos en `DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE`.
 */
export const getPersonDocumentRelationships = (person: PersonaStructure): BofuDocumentRelationship[] =>
  (person.person_types_detail || [])
    .filter((personType) => Object.hasOwn(DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE, personType.code))
    .flatMap((personType) => DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE[personType.code] || []);

/**
 * Merge aditivo por `relationship`: genéricos (sin `related_to`) + extras de
 * `conyuge_aval` cuyo `related_to` está en `principalRelationships`
 * (p. ej. `representante_legal`, `obligado_solidario`). Empate de `order`: genérico primero.
 */
export const resolveDocumentsByRelatedTo = (
  tabDocuments: TabsConfigResponseDocument[],
  personRelationships: ReadonlySet<BofuDocumentRelationship>,
  principalRelationships: ReadonlySet<BofuDocumentRelationship> = new Set(),
): TabsConfigResponseDocument[] => {
  const scoped = tabDocuments.filter(
    (document) =>
      validateDocumentStructure(document) &&
      document.relationship !== undefined &&
      personRelationships.has(document.relationship),
  );

  const base = scoped.filter((document) => !document.related_to);
  const extras = scoped.filter(
    (document) =>
      document.relationship === "conyuge_aval" &&
      document.related_to !== undefined &&
      principalRelationships.has(document.related_to),
  );

  return [...base, ...extras].sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Arma los documentos de una persona según sus tipos, ordenados por `order`, y les asigna
 * los archivos ya cargados de esa persona (mismo file code y `person_id`).
 *
 * Para cónyuge: pasa `allPersons` para resolver `related_to` (documentos adicionales
 * según el tipo del principal que la tiene en `spouse_id`).
 */
export const buildPersonDocuments = ({
  person,
  tabDocuments,
  uploadedDocuments,
  clientDetailsId,
  allPersons = [],
}: {
  person: PersonaStructure;
  tabDocuments: TabsConfigResponseDocument[];
  uploadedDocuments: DocumentResponse[];
  clientDetailsId: UUID;
  /** Personas del hub; necesario para merge de `related_to` en cónyuge. */
  allPersons?: PersonaStructure[];
}): BofuTabDocument[] => {
  const personRelationships = new Set(getPersonDocumentRelationships(person));
  const principalOfPerson = allPersons.find((candidate) => candidate.spouse_id === person.id);
  const principalRelationships = new Set(principalOfPerson ? getPersonDocumentRelationships(principalOfPerson) : []);

  const documents = resolveDocumentsByRelatedTo(tabDocuments, personRelationships, principalRelationships).map(
    (docItem) => generateDocumentConfig({ docItem, docs_aval: [], clientDetailsId: undefined, aval: undefined }),
  );

  return generatePropietarioDocs({
    personItem: person,
    valid_documents: documents,
    documents_uploaded: uploadedDocuments,
    clientDetailsId,
  });
};

export type PersonDocumentChange = {
  document: BofuTabDocument;
  newValue: DocumentResponse | undefined;
  valueAction?: "add" | "remove" | "update";
  valueIndex?: number;
};

/** Identifica el mismo documento por file code y relación, para no mezclar tipos de la misma persona. */
const isSameDocumentSlot = (current: BofuTabDocument, target: BofuTabDocument) =>
  current.file_category_code === target.file_category_code && current.relationship === target.relationship;

/**
 * Aplica un cambio sobre los documentos locales de la persona, sin escribir en el store de BOFU.
 * Documento simple: reemplaza `file_value`. Múltiple: agrega, actualiza o quita en `list_values` según `valueAction`.
 * Regresa un arreglo nuevo; los documentos sin cambios conservan su referencia.
 */
export const applyPersonDocumentChange = (
  documents: BofuTabDocument[],
  change: PersonDocumentChange,
): BofuTabDocument[] =>
  documents.map((document) => {
    if (!isSameDocumentSlot(document, change.document)) return document;

    if (!change.document.multiple) {
      return { ...document, file_value: change.newValue };
    }

    const listValues = [...document.list_values];

    if (change.valueAction === "add") {
      listValues.push(change.newValue);
    } else if (change.valueAction === "update" && change.valueIndex !== undefined) {
      listValues[change.valueIndex] = change.newValue;
    } else if (change.valueAction === "remove" && change.valueIndex !== undefined) {
      listValues.splice(change.valueIndex, 1);
    }

    return { ...document, list_values: listValues };
  });

const cloneDisabledFields = (): Record<PersonFormFieldCode, PersonFormFieldConfig> => ({
  ...ALL_FIELDS_DISABLED,
});

/**
 * Combina un campo del default con el del API.
 * Si el API no trae el campo, se usa el default (o deshabilitado si tampoco existe).
 * Si lo trae, `enabled` viene del API y `required` del API o, si no viene, del default.
 */
const mergeFieldConfig = (
  defaultField: PersonFormFieldConfig | undefined,
  apiField: PersonFormFieldConfig | undefined,
): PersonFormFieldConfig => {
  if (!apiField) {
    return defaultField ?? { enabled: false };
  }
  return {
    enabled: apiField.enabled,
    ...(apiField.required !== undefined
      ? { required: apiField.required }
      : defaultField?.required !== undefined
        ? { required: defaultField.required }
        : {}),
  };
};

/**
 * Combina un nodo (tipo de persona) del default con el del API.
 * Sin nodo del API regresa una copia del default. Con nodo: `code`/`label` del API o default,
 * campos mergeados uno a uno, y `relations` del API si vienen (p. ej. PTCAV).
 * `enabled` se conserva por compatibilidad pero el portal lo ignora al resolver campos.
 */
const mergeNodeConfig = (
  defaultNode: PersonFormNodeConfigResponse,
  apiNode: PersonFormNodeConfigResponse | undefined,
): PersonFormNodeConfigResponse => {
  if (!apiNode) {
    return {
      ...defaultNode,
      fields: { ...defaultNode.fields },
      ...(defaultNode.relations ? { relations: structuredClone(defaultNode.relations) } : {}),
    };
  }

  const mergedFields: Record<string, PersonFormFieldConfig> = cloneDisabledFields();

  PERSON_FORM_FIELD_CODES.forEach((fieldCode) => {
    mergedFields[fieldCode] = mergeFieldConfig(defaultNode.fields[fieldCode], apiNode.fields?.[fieldCode]);
  });

  return {
    code: apiNode.code || defaultNode.code,
    label: apiNode.label ?? defaultNode.label,
    enabled: apiNode.enabled ?? defaultNode.enabled,
    fields: mergedFields,
    ...(apiNode.relations
      ? { relations: apiNode.relations }
      : defaultNode.relations
        ? { relations: structuredClone(defaultNode.relations) }
        : {}),
  };
};

/**
 * Obtiene la config de formularios por persona a partir de `tabs_bofu_config`.
 * Sin FORMULARIOS_PERSONA o config vacía → default local.
 * Con config parcial del API → merge sobre el default (nodo y campo).
 * Nodos que solo existen en el API se agregan con sus campos sobre la base deshabilitada.
 */
export const resolvePersonFormsConfig = (forms?: Array<TabsConfigResponseForm>): PersonFormsConfig => {
  const formEntry = forms?.find((form) => form.code === BOFU_TABS_FORM_CODES.formularios_persona);
  const apiConfig = formEntry?.config;

  if (!apiConfig || Object.keys(apiConfig).length === 0) {
    return structuredClone(DEFAULT_PERSON_FORMS_CONFIG);
  }

  const resolved: PersonFormsConfig = {};

  Object.entries(DEFAULT_PERSON_FORMS_CONFIG).forEach(([nodeCode, defaultNode]) => {
    resolved[nodeCode] = mergeNodeConfig(defaultNode, apiConfig[nodeCode]);
  });

  Object.entries(apiConfig).forEach(([nodeCode, apiNode]) => {
    if (!resolved[nodeCode] && apiNode) {
      resolved[nodeCode] = {
        code: apiNode.code || nodeCode,
        label: apiNode.label,
        enabled: apiNode.enabled,
        fields: { ...cloneDisabledFields(), ...(apiNode.fields || {}) },
        ...(apiNode.relations ? { relations: apiNode.relations } : {}),
      };
    }
  });

  return resolved;
};

/**
 * Entre los códigos del principal, elige el de mayor prioridad en el hub que tenga
 * variante en `relations`. `undefined` si ninguno califica.
 */
const pickPrincipalRelationCode = (
  principalTypeCodes: string[],
  relations: Record<string, { fields: Record<string, PersonFormFieldConfig> }> | undefined,
): string | undefined => {
  if (!relations || !principalTypeCodes.length) return undefined;

  let bestCode: string | undefined;
  let bestIndex = Number.POSITIVE_INFINITY;

  for (const code of principalTypeCodes) {
    if (!Object.hasOwn(relations, code)) continue;
    const index = getFormHubTypeIndex(code);
    if (index !== -1 && index < bestIndex) {
      bestIndex = index;
      bestCode = code;
    }
  }

  return bestCode;
};

/**
 * Fields que aporta el nodo PTCAV: `relations[principal]` (reemplazo total) o `PTCAV.fields`.
 */
const resolveSpouseNodeFields = (
  ptcavNode: PersonFormNodeConfigResponse,
  principalTypeCodes: string[] = [],
): Record<string, PersonFormFieldConfig> => {
  const relationCode = pickPrincipalRelationCode(principalTypeCodes, ptcavNode.relations);
  if (relationCode && ptcavNode.relations?.[relationCode]?.fields) {
    return ptcavNode.relations[relationCode].fields;
  }
  return ptcavNode.fields || {};
};

/**
 * Calcula los campos a mostrar/validar para una persona según sus tipos.
 *
 * Capas:
 * 1. `formsConfig` ya debe venir de `resolvePersonFormsConfig` (API mergeado sobre default local por nodo).
 *    Si no se pasa o está vacío, se usa `DEFAULT_PERSON_FORMS_CONFIG`.
 * 2. Unión entre los tipos de la misma persona (p. ej. PTRL + PTAM) con OR:
 *    - `enabled` = true si algún nodo lo tiene enabled
 *    - `required` = true si algún nodo lo marca required
 *    - `node.enabled` se ignora (contrato admin).
 * 3. Para PTCAV: si hay `principalTypeCodes` y existe `relations[codigo]`, esa mapa
 *    reemplaza por completo `PTCAV.fields` como contribución del cónyuge.
 * 4. Si tiene PTCAV, se retiran `civil_status_id`, `matrimonial_regimes_id` y `spouse_id`.
 *
 * Ejemplo PTRL + PTAM (defaults): celular/email/rfc del PTRL + ocupación/estado civil/régimen/cónyuge del PTAM.
 * Codes sin nodo en `formsConfig` se ignoran.
 */
export const resolveFieldsForPerson = (
  personTypeCodes: string[],
  formsConfig?: PersonFormsConfig | null,
  principalTypeCodes: string[] = [],
): Record<PersonFormFieldCode, PersonFormFieldConfig> => {
  if (!personTypeCodes.length) {
    return cloneDisabledFields();
  }

  const config = formsConfig && Object.keys(formsConfig).length > 0 ? formsConfig : DEFAULT_PERSON_FORMS_CONFIG;
  const resolved = cloneDisabledFields();
  const spouseCode = PERSON_CATALOG.conyuge_aval.code;

  const knownTypeCodes = personTypeCodes.filter((typeCode) => Boolean(config[typeCode]));

  knownTypeCodes.forEach((typeCode) => {
    const node = config[typeCode];
    if (!node) {
      return;
    }

    const nodeFields =
      typeCode === spouseCode ? resolveSpouseNodeFields(node, principalTypeCodes) : node.fields || {};

    PERSON_FORM_FIELD_CODES.forEach((fieldCode) => {
      const field = nodeFields[fieldCode];
      if (!field?.enabled) {
        return;
      }

      resolved[fieldCode] = {
        enabled: true,
        required: Boolean(resolved[fieldCode].required || field.required),
      };
    });
  });

  if (personTypeCodes.includes(spouseCode)) {
    PTCAV_EXCLUDED_FIELD_CODES.forEach((fieldCode) => {
      resolved[fieldCode] = { enabled: false };
    });
  }

  return resolved;
};

/** Indica si hay al menos un campo habilitado para renderizar el formulario. */
export const hasEnabledPersonFields = (fields: Record<PersonFormFieldCode, PersonFormFieldConfig>): boolean =>
  PERSON_FORM_FIELD_CODES.some((code) => fields[code]?.enabled);
